import classNames from "classnames";
import Icon from "~/components/shared/Icon";
import { useSteps } from "~/components/build/Show/lib/useSteps";

const variants = {
  warning: {
    title: "Unresolved step dependencies",
    body: "This step is waiting for the following dependencies to resolve before running, but they do not currently appear in the pipeline:",
    icon: "heroicons/16/solid/exclamation-triangle",
    borderColor: "border-orange-500",
    bgColor: "bg-orange-100",
    iconColor: "text-orange-500",
  },
  error: {
    title: "Step dependencies not found",
    body: "The following step dependencies were not found during the build:",
    icon: "heroicons/16/solid/x-mark",
    borderColor: "border-red-500",
    bgColor: "bg-red-500",
    iconColor: "text-white",
  },

  group_error: {
    title: "Group step dependencies not found",
    body: "The following group step dependencies were not found during the build:",
    icon: "heroicons/16/solid/x-mark",
    borderColor: "border-red-500",
    bgColor: "bg-red-500",
    iconColor: "text-white",
  },

  group_warning: {
    title: "Unresolved group step dependencies",
    body: "The following group step dependencies could not be found during the build:",
    icon: "heroicons/16/solid/exclamation-triangle",
    borderColor: "border-orange-500",
    bgColor: "bg-orange-100",
    iconColor: "text-orange-500",
  },
};

export function MissingStepDependencies({
  variant = "warning",
  stepUuid,
  group = false,
}: {
  variant?: keyof typeof variants;
  stepUuid: string;
  group?: boolean;
}) {
  if (group) {
    variant = `group_${variant}` as keyof typeof variants;
  }

  const { stepsMap } = useSteps();
  const step = stepsMap.get(stepUuid);

  if (!step) {
    return null;
  }

  const { missingDependencies = [] } = step;

  const dependencies = missingDependencies || [];

  if (!dependencies.length) {
    return null;
  }

  const { borderColor, bgColor, icon, iconColor, title, body } = variants[variant];

  return (
    <div className={classNames("mt-4 flex overflow-hidden rounded border", borderColor)}>
      <div
        className={classNames(
          "flex w-8 flex-shrink-0 justify-center border-r pt-4",
          borderColor,
          bgColor,
        )}
      >
        <Icon icon={icon} className={classNames("h-4 w-4", iconColor)} />
      </div>

      <div className="flex-grow px-4 py-3">
        <h3 className="mb-2 mt-0 text-base">{title}</h3>

        <p className="mb-3 mt-0">{body}</p>

        <ul className="depth mb-3 max-w-lg divide-y rounded">
          {dependencies.map((dependency) => (
            <li key={dependency} className="monospace m-0 border-gray-300 p-2.5 text-xs">
              {dependency}
            </li>
          ))}
        </ul>

        <p className="m-0">
          Check the pipeline is configured correctly, or see the{" "}
          <a href="/docs/pipelines/dependencies" className="text-inherit underline">
            documentation
          </a>
          .
        </p>
      </div>
    </div>
  );
}
